import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';

// Custom hook for Google Analytics tracking
export const useAnalytics = () => {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());
  const scrollDepthTracked = useRef(new Set());

  // Track page views when location changes
  useEffect(() => {
    const pagePath = location.pathname;
    const pageTitle = getPageTitle(pagePath);
    
    // Track page view
    analyticsService.trackPageView(pagePath, pageTitle);
    
    // Reset tracking states for new page
    pageStartTime.current = Date.now();
    scrollDepthTracked.current.clear();
    
    // Track time spent on previous page
    return () => {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      if (timeSpent > 5) { // Only track if user spent more than 5 seconds
        analyticsService.trackTimeOnPage(pagePath, timeSpent);
      }
    };
  }, [location]);

  // Scroll depth tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollDepth = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      
      // Track scroll milestones (25%, 50%, 75%, 100%)
      const milestones = [25, 50, 75, 100];
      milestones.forEach(milestone => {
        if (scrollDepth >= milestone && !scrollDepthTracked.current.has(milestone)) {
          scrollDepthTracked.current.add(milestone);
          analyticsService.trackScrollDepth(milestone, location.pathname);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Error tracking
  useEffect(() => {
    const handleError = (event) => {
      analyticsService.trackError(
        event.error?.message || 'Unknown error',
        location.pathname,
        'javascript'
      );
    };

    const handleUnhandledRejection = (event) => {
      analyticsService.trackError(
        event.reason?.message || 'Unhandled promise rejection',
        location.pathname,
        'promise'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [location.pathname]);

  return {
    trackEvent: analyticsService.trackEvent.bind(analyticsService),
    trackButtonClick: analyticsService.trackButtonClick.bind(analyticsService),
    trackLinkClick: analyticsService.trackLinkClick.bind(analyticsService),
    trackFormStart: analyticsService.trackFormStart.bind(analyticsService),
    trackFormSubmit: analyticsService.trackFormSubmit.bind(analyticsService),
    trackFormFieldFocus: analyticsService.trackFormFieldFocus.bind(analyticsService),
    trackDashboardInteraction: analyticsService.trackDashboardInteraction.bind(analyticsService),
    trackChartInteraction: analyticsService.trackChartInteraction.bind(analyticsService),
    trackContactMethod: analyticsService.trackContactMethod.bind(analyticsService),
    trackSocialMediaClick: analyticsService.trackSocialMediaClick.bind(analyticsService),
    trackContentInteraction: analyticsService.trackContentInteraction.bind(analyticsService),
    trackEngagement: analyticsService.trackEngagement.bind(analyticsService)
  };
};

// Enhanced click tracking hook
export const useClickTracking = () => {
  const location = useLocation();

  const trackClick = useCallback((element, additionalData = {}) => {
    const buttonText = element.textContent || element.innerText || element.alt || 'Unknown';
    const buttonLocation = location.pathname;
    const elementType = element.tagName?.toLowerCase();
    const elementClass = element.className;
    const elementId = element.id;

    analyticsService.trackButtonClick(buttonText, buttonLocation, {
      element_type: elementType,
      element_class: elementClass,
      element_id: elementId,
      ...additionalData
    });
  }, [location.pathname]);

  return { trackClick };
};

// Form tracking hook
export const useFormTracking = (formName) => {
  const location = useLocation();
  const formStarted = useRef(false);

  const trackFormStart = useCallback(() => {
    if (!formStarted.current) {
      formStarted.current = true;
      analyticsService.trackFormStart(formName, location.pathname);
    }
  }, [formName, location.pathname]);

  const trackFormSubmit = useCallback((success = true) => {
    analyticsService.trackFormSubmit(formName, location.pathname, success);
  }, [formName, location.pathname]);

  const trackFieldFocus = useCallback((fieldName) => {
    trackFormStart(); // Auto-track form start on first field focus
    analyticsService.trackFormFieldFocus(formName, fieldName);
  }, [formName, trackFormStart]);

  return {
    trackFormStart,
    trackFormSubmit,
    trackFieldFocus
  };
};

// Helper function to get page titles
function getPageTitle(pathname) {
  const titleMap = {
    '/': 'Home - Kunal Agarwal',
    '/trade-like-me': 'Trade Like Me - Kunal Agarwal',
    '/dashboard': 'Trading Dashboard - Kunal Agarwal',
    '/contact': 'Contact - Kunal Agarwal'
  };
  
  return titleMap[pathname] || `${pathname} - Kunal Agarwal`;
}

export default useAnalytics;